import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";

import { AppBar, Button, Coin, Eyebrow } from "../../src/components/brand";
import { Field, MoneyInput } from "../../src/components/controls";
import { FULL_DISCLAIMER, SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space } from "../../src/theme";

// deduction-method (WFH fixed-rate vs actual-cost) input screen — the
// biggest-audience deduction tool. Stateless-v1 (PLAN arch decision #1): we call
// the compute-only query and render the result INLINE on this screen (no
// save-by-id, no auth gate). The repo uses the VANILLA tRPC client — there is no
// useQuery hook — so we call `trpc.calc.deductionMethod.compute.query(payload)`
// in a submit handler and hold the result in useState.
//
// NOTE (expected): the `calc.deductionMethod` client key does not exist until
// the orchestrator stitches `deductionMethodRouter` into `calcRouter`. An
// isolated app typecheck will show one "calc.deductionMethod does not exist"
// error here until that stitch — this is expected per the PLAN.
//
// FY-keyed currency: the assessment year is passed to the API, which resolves
// the in-force fixed rate (70c FY2024-25/25-26, 67c earlier). We never show a
// hard-coded rate; every figure comes back from @aucash/rules.
//
// Info-not-advice (validation §3): the disclaimer renders ABOVE the first
// number; the bigger-deduction line is the API's conditional copy; no
// financial-product handoff on this personalised surface.

const ASSESSMENT_YEAR = "2025-26";

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.deductionMethod.compute.query>
>;

export default function DeductionMethodIndex(): JSX.Element {
  const router = useRouter();

  const [hours, setHours] = useState("");
  const [actualCost, setActualCost] = useState("");
  const [taxableIncome, setTaxableIncome] = useState("");

  const [errors, setErrors] = useState<{ hours?: string; income?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const onSubmit = (): void => {
    const next: { hours?: string; income?: string } = {};
    if (hours.trim() === "") {
      next.hours = "Enter your total hours worked from home for the year.";
    }
    if (taxableIncome.trim() === "") {
      next.income = "Enter your taxable income so we can value the deduction.";
    }
    setErrors(next);
    if (Object.keys(next).length > 0 || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    trpc.calc.deductionMethod.compute
      .query({
        input: {
          workFromHomeHours: Number(hours),
          actualCostDeduction: Number(actualCost || "0"),
          taxableIncome: Number(taxableIncome),
          fy: ASSESSMENT_YEAR,
        },
      })
      .then((res) => {
        setResult(res);
      })
      .catch((err: unknown) => {
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't work that out.",
        );
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Work from home" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>FY26 · fixed-rate vs actual-cost</Eyebrow>
            <Text style={styles.title}>
              Which work-from-home method gives the bigger deduction?
            </Text>
            <Text style={styles.subtitle}>
              Compare the ATO fixed-rate method (cents per hour worked from home)
              against your actual-cost claim for FY26, using the current ATO rate.
            </Text>
            {/* Disclaimer renders ABOVE the first number (validation §3.2). */}
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field
            label="Hours worked from home this year (FY26)"
            help="Total work hours from home for the year. The ATO fixed-rate method multiplies these by the current cents-per-hour rate, which covers energy, phone, internet and stationery."
            error={errors.hours}
          >
            <MoneyInput
              value={hours}
              onChange={setHours}
              testID="input-wfh-hours"
              accessibilityLabel="Hours worked from home this year"
            />
          </Field>

          <Field
            label="Your actual-cost claim (optional)"
            help="The work-related portion of your actual running costs you've worked out yourself — only include this if you've kept the records and receipts. Leave blank if you haven't worked one out."
          >
            <MoneyInput
              value={actualCost}
              onChange={setActualCost}
              testID="input-actual-cost"
              accessibilityLabel="Your actual-cost claim"
            />
          </Field>

          <Field
            label="Your taxable income for FY26"
            help="Used to estimate what the deduction is worth after tax — at your tax bracket plus the Medicare levy. We don't store this."
            error={errors.income}
          >
            <MoneyInput
              value={taxableIncome}
              onChange={setTaxableIncome}
              testID="input-taxable-income"
              accessibilityLabel="Your taxable income for FY26"
            />
          </Field>

          <Button
            label={submitting ? "Working it out…" : "Compare the methods →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="deduction-method-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {result !== null ? (
            <View style={styles.result} testID="deduction-method-result">
              {/* §3.2: full disclaimer at the top of the result, before any number. */}
              <View style={styles.disclaimerBlock}>
                <Text style={styles.disclaimerText}>{FULL_DISCLAIMER}</Text>
              </View>

              <Eyebrow>Your result · estimate</Eyebrow>

              <View style={styles.headlineCard}>
                <Text style={styles.headline}>{result.output.copy.headline}</Text>
                <Text style={styles.bodyLine}>{result.output.copy.difference}</Text>
              </View>

              <View style={styles.grid}>
                {result.output.comparison.methods.map((m) => (
                  <View key={m.method} style={styles.cell}>
                    <Text style={styles.cellKey}>{LABEL[m.method]}</Text>
                    <Text style={styles.cellVal}>
                      {formatAud(m.deduction)}
                    </Text>
                  </View>
                ))}
              </View>

              <Text style={styles.bodyLine}>{result.output.copy.value}</Text>
              <Text style={styles.sourceLine}>{result.output.copy.source}</Text>
            </View>
          ) : null}
        </View>
      </ScrollView>
    </View>
  );
}

const LABEL: Record<string, string> = {
  "fixed-rate": "Fixed-rate method",
  "actual-cost": "Actual-cost method",
  "cents-per-km": "Cents-per-km method",
  logbook: "Logbook method",
};

/** Whole-dollar AUD, mirroring the rules-layer formatter (no decimals). */
function formatAud(n: number): string {
  return `$${Math.round(n).toLocaleString("en-AU")}`;
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  submitError: { fontFamily: fonts.bodyMedium, fontSize: 14, color: colors.danger, textAlign: "center" },
  result: {
    gap: space.lg,
    padding: space.lg,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    backgroundColor: colors.sand,
  },
  disclaimerBlock: {
    backgroundColor: colors.cream,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.sm,
    padding: space.md,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  headlineCard: { gap: space.sm },
  headline: {
    fontFamily: fonts.display,
    fontSize: 20,
    lineHeight: 26,
    color: colors.ink,
  },
  grid: { flexDirection: "row", gap: space.md },
  cell: {
    flex: 1,
    padding: space.md,
    borderRadius: radius.sm,
    backgroundColor: colors.cream,
    borderWidth: 1,
    borderColor: colors.line,
    gap: space.xs,
  },
  cellKey: { fontFamily: fonts.bodySemibold, fontSize: 12, color: colors.muted },
  cellVal: { fontFamily: fonts.display, fontSize: 20, color: colors.ink },
  bodyLine: { fontFamily: fonts.body, fontSize: 14, lineHeight: 20, color: colors.inkSoft },
  sourceLine: { fontFamily: fonts.body, fontSize: 12, lineHeight: 17, color: colors.muted },
});
